import React, { useEffect, useRef, useState } from 'react';

import { AvatarGroup, Box, Menu, MenuItem, MenuList } from '@mui/material';
import { uniqueId } from 'lodash';

import type { PartialUser } from '../../../Data/types';
import MLPersona from '../MLPersona/MLPersona';

type Props = {
  items: PartialUser[];
  avatarSize?: number;
};

const OverflowAvatar = ({ items, avatarSize = 32 }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxAvatars, setMaxAvatars] = useState(items.length);
  const [overflowAvatars, setOverflowAvatars] = useState<PartialUser[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const resizeHandler = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      let availableWidth = containerWidth;
      const tempVisible: PartialUser[] = [];

      items.forEach((item) => {
        const avatarWidth = avatarSize + 8; // 8px for margin
        // Check if the avatar fits in the available width
        if (availableWidth - avatarWidth > 40) {
          // Leave space for +more avatar
          tempVisible.push(item);
          availableWidth -= avatarWidth;
        }
      });

      setMaxAvatars(tempVisible.length);
      setOverflowAvatars(items.slice(tempVisible.length - 1));
    };

    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [items, avatarSize]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box ref={containerRef} sx={{ display: 'flex', width: '100%' }}>
      {items?.length === 1 && <MLPersona user={items[0]} key={uniqueId()} />}
      {items?.length > 1 && (
        <>
          <AvatarGroup
            max={maxAvatars}
            slotProps={{
              surplus: {
                sx: {
                  width: avatarSize,
                  height: avatarSize,
                  ':hover': { cursor: 'pointer' },
                },
                onClick: handleMenuOpen,
              },
            }}
          >
            {items.map((user) => (
              <MLPersona user={user} key={uniqueId()} />
            ))}
          </AvatarGroup>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            {...{ closeAfterTransition: true }}
          >
            <MenuList dense>
              {overflowAvatars.map((user) => (
                <MenuItem key={user.DisplayName} onClick={handleMenuClose}>
                  {user.DisplayName}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default OverflowAvatar;
